import { useCallback, useState } from 'react';
import useAsyncRetry from 'react-use/lib/useAsyncRetry';
import { forensicsApiRef } from '../api';
import { ErrorApiError, errorApiRef, useApi } from '@backstage/core-plugin-api';
import { useEntity } from '@backstage/plugin-catalog-react';

/**
 * Hook to expose a specific forensics item.
 * 
 * @param itemRef - the "prefix" reference for a specific forensics item.
 * @param pod - name of pod to find container on ('-' for default/0)
 * @param container - name of container to find logs for
 */
export function useContainerLog({
    itemRef,
    pod,
    container,
}: {
    itemRef: string,
    pod: string,
    container: string,
}) {
    const api = useApi(forensicsApiRef);
    const errorApi = useApi(errorApiRef);
    const { entity } = useEntity();

    const getContainerLog = useCallback(async () => {
        try {
            return api.getContainerLog({ prefix: itemRef, pod, container });
        } catch (e) {
            errorApi.post(e as ErrorApiError);
            return Promise.reject(e);
        }
    }, [itemRef, pod, container, entity, api, errorApi]);

    const { loading, value, retry } = useAsyncRetry(
        () => getContainerLog(),
        [getContainerLog]
    );

    return [
        { loading, value, retry },
        {
            getContainerLog,
        },
    ] as const;
}
